/**
 * .tuMetrics {"tu1": 0.93, "tu2": 0.95, "tu3": 1, "notes": "Per-stream message cache shape, key builder, and channel-health check shared by streamStore and its realtime modules", "tuVersion": "2.5"}
 * .help "Small, pure helpers for the stream message cache: how a stream is keyed, how a cache entry is written, and whether a realtime channel is still alive."
 */
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { Comment } from '../../types';

export type StreamCacheEntry = {
  messages: Comment[];
  hasMoreMessages: boolean;
  messageOffset: number;
  updatedAt: number;
};

/** Cached stream content older than this is shown immediately but refetched. */
export const CACHE_TTL_MS = 60 * 1000;

export const buildStreamKey = (
  streamType?: Comment['streamType'],
  recipientStreamId?: string
) => (streamType && recipientStreamId ? `${streamType}:${recipientStreamId}` : null);

export function isRealtimeChannelUsable(channel: RealtimeChannel | null | undefined): boolean {
  if (!channel) return false;
  const state = (channel as { state?: string }).state;
  return state === 'joined' || state === 'joining';
}

export type CacheHost = {
  messageCacheByStream: Record<string, StreamCacheEntry>;
  hasMoreMessages: boolean;
  messageOffset: number;
};

export const upsertStreamCacheEntry = (
  state: CacheHost,
  streamKey: string,
  messages: Comment[],
  overrides?: Partial<StreamCacheEntry>
): Record<string, StreamCacheEntry> => ({
  ...state.messageCacheByStream,
  [streamKey]: {
    messages,
    hasMoreMessages: overrides?.hasMoreMessages ?? state.hasMoreMessages,
    messageOffset: overrides?.messageOffset ?? state.messageOffset,
    updatedAt: Date.now(),
  },
});
